import React, { useContext, useCallback, useState, useEffect } from 'react';
import { TextField } from '../inputs/TextField';
import { EditorConfigButton } from './EditorConfigButton';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { EditorConfigButtons } from './EditorConfigButtons';
import { useEditorId } from './hooks/useEditorId';
import { EditorLabel } from './EditorLabel';
import { IEditorProps } from './Editor';

/**
 * Defined Type Editor.
 *
 * Copyright (c) 2019 Decisions All Rights Reserved
 */
export function EditorDefinedType({ child, path, onChange, hideInputLabel }: IEditorProps) {
  const context = useContext(PropertyEditorContext);
  const [id] = useEditorId(path);
  const [typeName, setTypeName] = useState<string | null>();

  // update value when it was changed outside from component
  useEffect(() => {
    setTypeName(child.Value && child.Value.OutputValue);
  }, [child.Value]);

  const onValueChange = useCallback(
    (value: string | null) => {
      setTypeName(value);
      onChange(value, path);
    },
    [path, onChange]
  );

  const onDrop = useCallback(() => {
    onValueChange(null);
  }, [onValueChange]);

  const openDataStructureEditorDialog = useCallback(
    async (entityId: string, actionName: string) => {
      context.onShowDialogue({
        node: child,
        path,
        editingContextId: context.editingContextId,
        name: actionName,
        options: {
          entityId: entityId,
          entityTypeName: 'DecisionsFramework.Design.DataStructure.DefinedDataStructure',
          onChange: onValueChange,
        },
      });
    },
    [child, context, onValueChange, path]
  );

  const onShowCreator = useCallback(() => {
    openDataStructureEditorDialog('DATA STRUCTURES FOLDER', 'creator');
  }, [openDataStructureEditorDialog]);

  const onShowEditor = useCallback(() => {
    openDataStructureEditorDialog(typeName || '', 'editor');
  }, [openDataStructureEditorDialog, typeName]);

  return (
    <EditorLabel htmlFor={id} editor={child} path={path} doNotWrap hideInputLabel={hideInputLabel}>
      <div className="dpc-editor-defined-type">
        <TextField disabled onClear={onDrop} showClear value={typeName || ''} />
        <EditorConfigButtons>
          <EditorConfigButton className="dpc-editor-defined-type__button" onClick={onShowCreator}>
            Create
          </EditorConfigButton>
          {!!typeName?.length && (
            <EditorConfigButton className="dpc-editor-defined-type__button" onClick={onShowEditor}>
              Edit
            </EditorConfigButton>
          )}
        </EditorConfigButtons>
      </div>
    </EditorLabel>
  );
}
